import { useGame } from "@/game/GameContext";
import { SUPPLIES } from "@/game/gameData";
import { ASH_MEDIA } from "@/game/media";
import PixelButton from "@/components/PixelButton";
import { sfx } from "@/game/audioEngine";
import { toast } from "sonner";

export default function SuppliesScreen() {
  const { state, dispatch } = useGame();
  const owned = new Set(state.supplies);
  const totalCost = state.supplies.reduce(
    (sum, sid) => sum + (SUPPLIES.find((s) => s.id === sid)?.cost || 0),
    0
  );
  const startingBudget = state.wheelOutcome?.budget || 0;
  const remaining = startingBudget - totalCost;

  const toggle = (item) => {
    const has = owned.has(item.id);
    if (!has && remaining < item.cost) {
      toast.error("Not enough budget for that supply.");
      sfx.back();
      return;
    }
    sfx.buy();
    dispatch({ type: "TOGGLE_SUPPLY", id: item.id });
  };

  const startTrail = () => {
    // commit remaining budget into stats.budget for gameplay
    dispatch({ type: "SET_BUDGET", budget: remaining });
    dispatch({ type: "GOTO", screen: "month" });
  };

  return (
    <div className="min-h-screen px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
          <div>
            <h2 className="pixel-font text-xl md:text-2xl text-[#F26522]" data-testid="supplies-title">
              STARTER SUPPLIES
            </h2>
            <p className="vt323 text-xl text-white/80 mt-1">
              Stock the wagon. Choices here shape later months.
            </p>
          </div>
          <div className="pixel-panel-dark" data-testid="supplies-budget">
            <div className="pixel-font text-[10px] text-white/70">REMAINING</div>
            <div className="vt323 text-3xl text-emerald-300 tabular-nums">
              ${remaining.toLocaleString()}
            </div>
            <div className="text-xs text-white/60">of ${startingBudget.toLocaleString()}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SUPPLIES.map((s) => {
            const has = owned.has(s.id);
            const unaffordable = !has && remaining < s.cost;
            return (
              <button
                type="button"
                key={s.id}
                data-testid={`supply-${s.id}`}
                onClick={() => toggle(s)}
                className={`text-left bg-[#F4F0E6] border-4 ${
                  has ? "border-[#F26522]" : "border-black"
                } shadow-[4px_4px_0_0_#000] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_0_#000] transition-all p-3 disabled:opacity-60`}
                disabled={unaffordable}
              >
                <div className="flex items-start gap-2 mb-2">
                  <div className="relative">
                    <img src={ASH_MEDIA.crate} alt="" className="w-10 h-10 pixelated" />
                    <img
                      src={ASH_MEDIA.ash_logo_icon}
                      alt=""
                      aria-hidden="true"
                      className="absolute -bottom-1 -right-1 w-4 h-4 border border-black"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="pixel-font text-[10px] text-[#F58634]">{s.name}</div>
                  </div>
                  {has && (
                    <span className="pixel-font text-[9px] bg-[#F58634] text-white border-2 border-black px-1.5 py-0.5">
                      OWNED
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between bg-[#0F172A] text-white border-2 border-black px-2 py-1 mb-2">
                  <span className="pixel-font text-[9px] text-white/70">COST</span>
                  <span className="vt323 text-2xl tabular-nums text-[#F58634]">
                    ${s.cost.toLocaleString()}
                  </span>
                </div>
                <p className="text-xs leading-snug">{s.desc}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {s.tags.map((t) => (
                    <span key={t} className="text-[10px] bg-[#0F172A] text-white px-1.5 py-0.5">
                      {t}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row justify-between gap-3">
          <PixelButton variant="secondary" onClick={() => dispatch({ type: "GOTO", screen: "wheel" })}>
            ← Re-Spin (Lose Supplies)
          </PixelButton>
          <PixelButton data-testid="start-trail-button" onClick={startTrail}>
            Begin Month 1 ▶
          </PixelButton>
        </div>
      </div>
    </div>
  );
}
